"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import {alpha, Box, Stack, Typography} from "@mui/material";
import PlayCircleFilledWhiteOutlinedIcon from "@mui/icons-material/PlayCircleFilledWhiteOutlined";
import { MediaCard } from "@/types/dto/homepage/homepage-types";
import { WistiaPlayer } from "@wistia/wistia-player-react";
import sanitizeHtml from "@/utils/sanitize-html";

interface MediaCarouselCardProps {
  card: MediaCard;
  showOverlay?: boolean;
  index?: number;
}

const CAROUSEL_SELECTORS = {
  // eslint-disable-next-line @typescript-eslint/quotes
  rightArrow: '[data-testid="test-carousel-carousel-arrow-right"]',
  // eslint-disable-next-line @typescript-eslint/quotes
  leftArrow: '[data-testid="test-carousel-carousel-arrow-left"]',
  videoPlayer: "#media-carousel"
} as const;

const CARD_STYLES = {
  height: "512px",
  width: "288px",
  position: "relative",
  overflow: "hidden",
  borderRadius: "20px"
} as const;

const MediaCarouselCard: React.FC<MediaCarouselCardProps> = ({ card, showOverlay, index }) => {
  const {
    media: { wistiaVideoId: videoId, imageUrl } = {},
    overlayText: { headerText, copyText, subtitle } = {}
  } = card || {};

  const shouldShowBackgroundImage = !videoId && !!imageUrl;
  const [isOverlayVisible, setIsOverlayVisible] = useState(!!showOverlay);
  const playerRef = useRef<any>(null);

  function pauseAllVideos() {
    const videos = document.querySelectorAll(CAROUSEL_SELECTORS.videoPlayer);
    if (videos?.length) {
      videos.forEach((video: any) => {
        if (video?.state === "playing") {
          video?.pause();
        }
      });
    }
  }
  const handleOverlayClick = useCallback(() => {
    setIsOverlayVisible(false);
    playerRef.current?.play?.();
  }, []);

  // Setup carousel arrow event listeners
  useEffect(() => {
    pauseAllVideos();
  }, [index]);

  // Sync overlay visibility with prop
  useEffect(() => {
    setIsOverlayVisible(!!showOverlay);
  }, [showOverlay]);

  const cardStyles = {
    ...CARD_STYLES,
    pointerEvents: showOverlay ? "auto" : "none",
    ...(shouldShowBackgroundImage && imageUrl
      ? {
          backgroundImage: `url(${imageUrl})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat"
        }
      : {})
  };

  const renderVideoPlayer = () => {
    if (!videoId) return null;

    return (
      <WistiaPlayer
        id="media-carousel"
        ref={playerRef}
        mediaId={videoId}
        bigPlayButton={false}
        playBarControl={false}
        volumeControl={false}
        settingsControl={false}
        fullscreenControl={false}
        playPauseControl={false}
        playPauseNotifier={false}
        controlsVisibleOnLoad={false}
        seo
        style={{
          width: "100%",
          height: "100%",
          position: "absolute",
          objectFit: "cover",
          color: "var(--theme-palette-common-white)",
          scale: "1.01",
          top: 0,
          left: 0
        }}
      />
    );
  };

  const renderOverlay = () => {
    if (!showOverlay || !isOverlayVisible) {
      return null;
    }

    return (
      <Box
        data-testid={`overlay-box-${card.mediaCardId}`}
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          bgcolor: (theme) => alpha(theme.palette.common.black, 0.5),
          color: "common.white",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 2,
          padding: "32px 32px 100px 32px",
          zIndex: 2,
          textAlign: "center"
        }}
        tabIndex={0}
        role="button"
        aria-label="Play video"
      >
        {!shouldShowBackgroundImage && (
          <Box
            className="pseudo-absolute-full-container"
            onClick={handleOverlayClick}
            sx={{ zIndex: 3, height: "6rem" }}
          >
            <PlayCircleFilledWhiteOutlinedIcon
              data-testid="play-icon"
              sx={{
                fontSize: "6rem",
                fontWeight: 50
              }}
            />
          </Box>
        )}
        <Stack spacing="5px">
          {headerText && (
            <Typography
              variant="h4"
              data-testid={`header-text-${card.mediaCardId}`}
              sx={{ fontSize: "24px", fontWeight: 600, textAlign: "center" }}
            >
              {headerText}
            </Typography>
          )}
          {subtitle && (
            <Typography
              data-testid={`subtitle-text-${card.mediaCardId}`}
              sx={{
                zIndex: 4,
                cursor: "auto",
                fontSize: 16,
                fontWeight: 600,
                textAlign: "center",
                // Style anchor tags within this Typography
                "& a": {
                  color: "common.white", // or any color you want
                  textDecoration: "underline !important",
                  fontWeight: 600
                }
              }}
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(subtitle?.replace(/<p>/g, "")) }}
            />
          )}
          {copyText && (
            <Box sx={{ fontSize: 16, fontWeight: 400, textAlign: "center" }}>{copyText}</Box>
          )}
        </Stack>
      </Box>
    );
  };

  return (
    <Box data-testid={`media-carousel-card-${card.mediaCardId}`} sx={{ ...cardStyles }}>
      {renderVideoPlayer()}
      {renderOverlay()}
    </Box>
  );
};

export default MediaCarouselCard;
